import React from "react";
import { View, StyleSheet } from "react-native";
import NtRootPageContainer from "../../root/components/NtRootPageContainer";
import BaseController from "../../common/base/BaseController";
import NtPageActionButton from "../../components/NtPageActionButton";
import NtListHeader from "../../components/NtListHeader";
import NtListEmpty from "../../components/NtListEmpty";
import NtPageSection from "../../components/NtPageSection";
import NtPagination from "../../components/NtPagination";
import NtSearchBar from "../../components/NtSearchBar";
import NtModal from "../../components/NtModal";
import {
  asyncStorageSetData,
  showToast,
} from "../../common/utils/ControllerUtils";
import numbercsvcolumndata from "../data/numbercsvcolumndata";
import Logger from "../../common/utils/Logger";
import settings from "../../config/settings";
import { fetchAllNumbers, HAL_PAGE_SIZE } from "../../api/number";
import { fetchInboundRoutes } from "../../api/inboundroute";
import { digits10 } from "../utils/numberUtils";
import NtNumberItem from "../components/NtNumberItem";
import NtNumberNewForm from "../components/NtNumberNewForm";

class NumberController extends BaseController {
  state = {
    ...super.state,
    loading: false,
    // allData holds every enriched DID; `data` is the filtered + paged slice
    // shown in the table. The DID list has no server search, so we filter and
    // paginate client-side.
    allData: [],
    data: [],
    totalItems: 0,
    searchText: "",
    page: 1,
  };

  column_location = "@number_column_storage";
  addAccessModalRef = React.createRef();

  componentDidMount() {
    this.handleDefaultColumnsFetch(
      this.column_location,
      numbercsvcolumndata.filter((each) => each.default === true),
    );
    this.handleDataFetch();
  }

  handleDataFetch = async () => {
    this.setState({ loading: true });
    const domain = this.context.user?.domain;
    // Fetch every DID + the tenant's inbound routes so we can show each number's
    // destination. The DID list has no routing info — that lives on the PBX side.
    const [response, routesRes] = await Promise.all([
      fetchAllNumbers(),
      fetchInboundRoutes(domain, 1, 1000, null).catch(() => null),
    ]);
    Logger("Numbers fetch response", response);

    if (response.ok) {
      const routeMap = {};
      if (routesRes?.ok) {
        (routesRes.data?.data || []).forEach((r) => {
          routeMap[digits10(r.dialplan_number)] = {
            action: r.dialplan_destination_actions,
            description: r.dialplan_description,
            // dialplan_enabled is boolean-ish; only an explicit false is "off".
            enabled: String(r.dialplan_enabled).toLowerCase() !== "false",
          };
        });
      }
      const allData = (response.data?.data || []).map((did) => {
        const rt = routeMap[digits10(did.did)];
        return {
          ...did,
          routeAction: rt?.action || null,
          // null when unrouted, true/false when a route exists — drives the
          // Disabled badge in the list.
          routeEnabled: rt ? rt.enabled : null,
          // Show the inbound-route description (what the Manage modal edits) —
          // the DID's own `description` field isn't editable and stays empty.
          routeDescription: rt?.description || did.description || "",
        };
      });
      this.setState({ allData }, this.applyView);
    } else {
      showToast(
        "Couldn't load numbers",
        "Please try again in a moment.",
        "error",
      );
    }
    this.setState({ loading: false });
  };

  // Filter the full set by the search text, then slice to the current page.
  // Matches the number (by digits), the routing description, and the
  // destination label so a search finds a number by any of them.
  handleFilter = (rows) => {
    const q = this.state.searchText.trim().toLowerCase();
    if (!q) return rows;
    const digitsQ = q.replace(/\D/g, "");
    return rows.filter((row) => {
      const numMatch = digitsQ && String(row.did || "").includes(digitsQ);
      const descMatch = (row.routeDescription || "").toLowerCase().includes(q);
      const a = row.routeAction;
      const routeMatch =
        a && `${a.name || ""} ${a.value || ""}`.toLowerCase().includes(q);
      return numMatch || descMatch || routeMatch;
    });
  };

  applyView = () => {
    const filtered = this.handleFilter(this.state.allData);
    const start = (this.state.page - 1) * HAL_PAGE_SIZE;
    this.setState({
      data: filtered.slice(start, start + HAL_PAGE_SIZE),
      totalItems: filtered.length,
    });
  };

  handleSearch = (text) => {
    this.setState({ searchText: text, page: 1 }, this.applyView);
  };

  handleExportFetch = async () => this.handleFilter(this.state.allData);

  handleExportDataParce = (item, column) => {
    if (column === "route") {
      const a = item.routeAction;
      return a ? [a.name, a.value].filter(Boolean).join(": ") : "";
    }
    if (column === "description") return item.routeDescription || "";
    return item[column];
  };

  handlePageChange = (nextPage) => {
    this.setState({ page: nextPage }, this.applyView);
  };

  renderAddNewForm = () => (
    <NtModal ref={this.addAccessModalRef}>
      <NtNumberNewForm
        onCancel={() => this.addAccessModalRef.current?.dismiss()}
        onCreate={(tn) => {
          this.setState({ page: 1 }, () => this.handleDataFetch());
          this.addAccessModalRef.current?.dismiss();
          showToast("Success", `Added number ${tn || ""}`.trim());
        }}
      />
    </NtModal>
  );

  renderRow = (item, index) => (
    <NtNumberItem
      key={index}
      item={item}
      columns={this.state.columns}
      canEdit={this.handlePermissionCheck(settings.permissionTypeEdit)}
      onUpdate={() => this.handleDataFetch()}
      onDelete={() => {
        this.setState({ page: 1 }, () => this.handleDataFetch());
        showToast("Success", `Released number ${item?.did}`);
      }}
    />
  );

  render() {
    const canEdit = this.handlePermissionCheck(settings.permissionTypeEdit);
    const count = this.state.totalItems || 0;
    const subtitle = `Phone numbers attached to your account · ${count} number${count === 1 ? "" : "s"}`;

    const actions = canEdit ? (
      <NtPageActionButton
        icon="plus"
        title="Get Number"
        variant="primary"
        onPress={() => this.addAccessModalRef.current?.show()}
      />
    ) : null;

    const searchBar = (
      <NtSearchBar
        value={this.state.searchText}
        onChangeText={this.handleSearch}
        placeholder="Search by number, description, or destination"
      />
    );

    return (
      <NtRootPageContainer
        pageTitle="Numbers"
        pageSubtitle={subtitle}
        permission={this.handlePermissionCheck()}
        actions={actions}
      >
        <NtPageSection
          search={searchBar}
          footer={
            <NtPagination
              page={this.state.page}
              pageSize={HAL_PAGE_SIZE}
              total={this.state.totalItems}
              onChange={this.handlePageChange}
            />
          }
        >
          <View style={styles.tableWrap}>
            <NtListHeader
              visible={this.state.data?.length > 0}
              headerContainerStyle={{ marginRight: canEdit ? 35 : 0 }}
              columns={numbercsvcolumndata}
              selectedColumns={this.state.columns}
              onColumnUpdate={(updated) => {
                asyncStorageSetData(this.column_location, updated);
                this.setState({ columns: updated });
              }}
              exportFileName="number"
              exportDataParser={this.handleExportDataParce}
              exportFetchDataAsync={this.handleExportFetch}
            />
            <NtListEmpty visible={this.state.data?.length === 0} />
            {this.state.data?.map((each, index) => this.renderRow(each, index))}
          </View>
        </NtPageSection>

        {this.renderAddNewForm()}
      </NtRootPageContainer>
    );
  }
}

const styles = StyleSheet.create({
  tableWrap: { paddingHorizontal: 14, paddingVertical: 4 },
});

export default NumberController;
